// ─────────────────────────────────────────────────────────────────────────
// Caisibo Mono Flat — token engine
//
// A single user-chosen accent (+ background) drives the whole palette. Every
// surface equals the background ("flat"); components separate via borders only.
// Tier/category colors are derived as an analogous hue spread off the accent,
// so they always stay in harmony with the two colors the user picked.
//
// The app's Tailwind tokens are consumed as `hsl(var(--token))`, i.e. they hold
// an "H S% L%" triplet. We derive hex values here and serialise to that form.
// ─────────────────────────────────────────────────────────────────────────
import type { SankeyNodeKind } from '../types'

export const MONO_DEFAULT_ACCENT = '#f5b82e'
export const MONO_DEFAULT_BG_DARK = '#0a0a0b'
export const MONO_DEFAULT_BG_LIGHT = '#f6f4ee'

// ── color math ───────────────────────────────────────────────────────────
type RGB = [number, number, number]

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v))
}

export function hexToRgb(hex: string): RGB {
  let h = (hex || '').replace('#', '').trim()
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return [
    parseInt(h.slice(0, 2), 16) || 0,
    parseInt(h.slice(2, 4), 16) || 0,
    parseInt(h.slice(4, 6), 16) || 0,
  ]
}

export function rgbToHex(rgb: RGB): string {
  return '#' + rgb.map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('')
}

/** Linear blend between two hex colors; t=0 → a, t=1 → b. */
export function mix(a: string, b: string, t: number): string {
  const A = hexToRgb(a)
  const B = hexToRgb(b)
  return rgbToHex([0, 1, 2].map((i) => A[i] + (B[i] - A[i]) * t) as unknown as RGB)
}

/** Perceived luminance, 0..1. Cheap brightness, used for light/dark decisions. */
export function luminance(hex: string): number {
  const c = hexToRgb(hex)
  return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255
}

/** WCAG 2.1 relative luminance — gamma-corrected, unlike `luminance`. */
export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio between two colours, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Text colour to put on a filled swatch.
 *
 * A brightness threshold gets this wrong in the middle of the range: a mid blue
 * like #4ea8ff reads as "bright" to a perceived-luminance test and gets white
 * text at 2.5:1. Measuring both candidates and taking the better one is the same
 * amount of work and cannot pick the losing side.
 */
export function bestOn(bg: string, dark = '#0a0a08', light = '#ffffff'): string {
  return contrastRatio(dark, bg) >= contrastRatio(light, bg) ? dark : light
}

export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255
  g /= 255
  b /= 255
  const mx = Math.max(r, g, b)
  const mn = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (mx + mn) / 2
  if (mx !== mn) {
    const d = mx - mn
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn)
    switch (mx) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4
    }
    h /= 6
  }
  return [h * 360, s, l]
}

export function hslToHex(h: number, s: number, l: number): string {
  h = (((h % 360) + 360) % 360) / 360
  let r: number
  let g: number
  let b: number
  if (s === 0) {
    r = g = b = l
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    const hue = (t: number): number => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }
    r = hue(h + 1 / 3)
    g = hue(h)
    b = hue(h - 1 / 3)
  }
  return rgbToHex([r * 255, g * 255, b * 255])
}

/** Serialise a hex color to the "H S% L%" form the Tailwind tokens expect. */
export function hexToHslString(hex: string): string {
  const [h, s, l] = rgbToHsl(...hexToRgb(hex))
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

// ── token derivation ───────────────────────────────────────────────────────
export interface MonoTokens {
  accent: string
  bg: string
  /** Card/popover surface. Flat → equal to bg; elevated → lifted off bg. */
  surface: string
  ink: string
  ink2: string
  ink3: string
  line: string
  line2: string
  acDim: string
  acSoft: string
  onAccent: string
  dark: boolean
  flat: boolean
  /** Per-tier analogous hues, used by the Sankey "colors" mode. */
  kindColor: Record<SankeyNodeKind, string>
}

// Analogous hue offsets off the accent, ordered so adjacent tiers stay distinct.
const KIND_HUE_OFFSET: Record<SankeyNodeKind, number> = {
  PRODUCT_TEAM: -44,
  OBJECTIVE: -20,
  SHARED_OBJECTIVE: -8,
  VALUE_STREAM: 0,
  BUSINESS_OPPORTUNITY: 28,
  DIVISION: 50,
  STRATEGIC_INTENT: 86,
  STRATEGIC_THEME: 116,
  UNIT: -64,
  UNALIGNED: 0, // overridden to a neutral grey below
}

// Guarantee the accent stays visible against the chosen background. If the two
// are too close in luminance (e.g. a black accent on a dark bg, or white on a
// light bg), nudge the accent's lightness away from the background so buttons,
// borders and accent fills never vanish.
function ensureAccentContrast(accent: string, bg: string): string {
  const lb = luminance(bg)
  if (Math.abs(luminance(accent) - lb) >= 0.28) return accent
  const [h, s] = rgbToHsl(...hexToRgb(accent))
  // On a dark bg, lift the accent clearly into the light range so its on-accent
  // text reads as dark (good contrast for the reversed E Ink "ink", etc.).
  return hslToHex(h, s, lb < 0.5 ? 0.7 : 0.42)
}

export function deriveMono(accentInput: string, bg: string, dark: boolean, flat = true): MonoTokens {
  const accent = ensureAccentContrast(accentInput, bg)
  const neutral = dark ? '#ffffff' : '#0d0b07'
  const ink = mix(accent, neutral, 0.85)
  // Muted text is still body text: at the old 0.5 it sat around 3:1 on light
  // backgrounds, which reads as washed out rather than secondary.
  const ink2 = mix(bg, ink, dark ? 0.6 : 0.66)
  const ink3 = mix(bg, ink, 0.4)
  const line = mix(bg, accent, dark ? 0.3 : 0.4)
  const line2 = mix(bg, accent, dark ? 0.16 : 0.22)
  const acDim = mix(bg, accent, dark ? 0.26 : 0.34)
  const acSoft = mix(bg, accent, dark ? 0.12 : 0.18)
  const onAccent = bestOn(accent)
  // Flat: surface == bg, separation via borders only. Elevated: a subtle lift.
  const surface = flat ? bg : (dark ? mix(bg, '#ffffff', 0.05) : mix(bg, '#ffffff', 0.6))

  const [h, s] = rgbToHsl(...hexToRgb(accent))
  const sat = clamp(s || 0.7, 0.52, 0.95)
  const lit = dark ? 0.62 : 0.46
  const kindColor = {} as Record<SankeyNodeKind, string>
  ;(Object.keys(KIND_HUE_OFFSET) as SankeyNodeKind[]).forEach((kind) => {
    kindColor[kind] = hslToHex(h + KIND_HUE_OFFSET[kind], sat, lit)
  })
  kindColor.UNALIGNED = mix(bg, ink, 0.45)

  return { accent, bg, surface, ink, ink2, ink3, line, line2, acDim, acSoft, onAccent, dark, flat, kindColor }
}

/**
 * Map derived tokens onto the app's CSS custom properties. Tailwind tokens get
 * "H S% L%" triplets; `--mono-*` helpers keep raw hex for SVG/canvas consumers.
 */
export function monoCssVars(tokens: MonoTokens): Record<string, string> {
  const { accent, bg, surface, ink, ink2, ink3, line, line2, acDim, acSoft, onAccent } = tokens
  const hsl = hexToHslString
  return {
    '--background': hsl(bg),
    '--foreground': hsl(ink),
    '--card': hsl(surface),
    '--card-foreground': hsl(ink),
    '--popover': hsl(surface),
    '--popover-foreground': hsl(ink),
    '--primary': hsl(accent),
    '--primary-foreground': hsl(onAccent),
    '--secondary': hsl(acSoft),
    '--secondary-foreground': hsl(ink),
    '--muted': hsl(acSoft),
    '--muted-foreground': hsl(ink2),
    '--accent': hsl(acSoft),
    '--accent-foreground': hsl(ink),
    '--border': hsl(line),
    '--input': hsl(line),
    '--ring': hsl(accent),
    '--sidebar-bg': hsl(bg),
    '--sidebar-fg': hsl(ink),
    '--sidebar-muted-fg': hsl(ink2),
    '--sidebar-border': hsl(line),
    '--sidebar-accent-bg': hsl(accent),
    '--sidebar-accent-alpha': '0.14',
    // raw hex helpers for SVG (Sankey patterns/legend) and canvas consumers
    '--mono-accent': accent,
    '--mono-bg': bg,
    '--mono-surface': surface,
    '--mono-on-accent': onAccent,
    '--mono-ink': ink,
    '--mono-ink-2': ink2,
    '--mono-ink-3': ink3,
    '--mono-line': line,
    '--mono-line-2': line2,
    '--mono-ac-soft': acSoft,
    '--mono-ac-dim': acDim,
  }
}

const MONO_VAR_KEYS = Object.keys(monoCssVars(deriveMono(MONO_DEFAULT_ACCENT, MONO_DEFAULT_BG_DARK, true)))

/** Remove every inline mono override so a non-mono theme's stylesheet wins. */
export function clearMonoCssVars(el: HTMLElement) {
  for (const key of MONO_VAR_KEYS) el.style.removeProperty(key)
}

// ── Caisibo Color — derived harmonized semantic palette ─────────────────────
// The color family keeps multi-hue meaning (green = good, red = bad …) but pulls
// every semantic hue toward the user's accent so the whole UI harmonises with
// their two chosen colors. Canonical hue is blended toward the accent, then
// saturation/lightness are normalised for the active background.
export type SemanticKind = 'positive' | 'warning' | 'negative' | 'info' | 'accent2'

export interface ColorTones {
  /** `Ink` is the tone as text; `On` is what goes on top of the solid fill. */
  positive: string; positiveSoft: string; positiveDim: string; positiveInk: string; positiveOn: string
  warning: string; warningSoft: string; warningDim: string; warningInk: string; warningOn: string
  negative: string; negativeSoft: string; negativeDim: string; negativeInk: string; negativeOn: string
  info: string; infoSoft: string; infoDim: string; infoInk: string; infoOn: string
  accent2: string; accent2Soft: string; accent2Dim: string; accent2Ink: string; accent2On: string
}

const CANONICAL: Record<SemanticKind, string> = {
  positive: '#22c55e',
  warning: '#f59e0b',
  negative: '#ef4444',
  info: '#3b82f6',
  accent2: '#8b5cf6',
}

function harmonize(canonical: string, accent: string, dark: boolean): string {
  const blended = mix(canonical, accent, 0.4) // pull the hue toward the accent
  const [h, s, l] = rgbToHsl(...hexToRgb(blended))
  const sat = clamp(s, 0.5, 0.92)
  const lit = dark ? clamp(l, 0.56, 0.7) : clamp(l, 0.4, 0.54)
  return hslToHex(h, sat, lit)
}

/**
 * Push a tone's lightness until it reads as text on `over`.
 *
 * A harmonised tone is tuned to look right as a *fill*; the same value used as
 * text on a pale tint of itself lands near 2:1. Hue and saturation are kept so
 * the semantic stays recognisable -- only lightness moves, and only as far as
 * it must.
 */
function readableOn(tone: string, over: string, min = 4.5): string {
  if (contrastRatio(tone, over) >= min) return tone
  const [h, s] = rgbToHsl(...hexToRgb(tone))
  const [, , l] = rgbToHsl(...hexToRgb(tone))
  const darken = luminance(over) > 0.5
  for (let step = 1; step <= 100; step += 1) {
    const nl = clamp(darken ? l - step / 100 : l + step / 100, 0, 1)
    const candidate = hslToHex(h, s, nl)
    if (contrastRatio(candidate, over) >= min) return candidate
    if (nl <= 0 || nl >= 1) break
  }
  return bestOn(over)
}

export function deriveColorTones(accent: string, bg: string, dark: boolean): ColorTones {
  const out = {} as Record<string, string>
  ;(Object.keys(CANONICAL) as SemanticKind[]).forEach((kind) => {
    const color = harmonize(CANONICAL[kind], accent, dark)
    const soft = mix(bg, color, dark ? 0.16 : 0.18)
    out[kind] = color
    out[`${kind}Soft`] = soft
    out[`${kind}Dim`] = mix(bg, color, dark ? 0.42 : 0.5)
    // Text colour for this semantic. Measured against the soft tint, which is
    // the harder of the two surfaces it appears on (the other is the page bg).
    out[`${kind}Ink`] = readableOn(color, soft)
    // Text colour to place on top of the solid fill.
    out[`${kind}On`] = bestOn(color)
  })
  return out as unknown as ColorTones
}

export function colorToneCssVars(tones: ColorTones): Record<string, string> {
  const vars: Record<string, string> = {}
  ;(Object.keys(CANONICAL) as SemanticKind[]).forEach((kind) => {
    const t = tones as unknown as Record<string, string>
    vars[`--cb-${kind}`] = t[kind]
    vars[`--cb-${kind}-soft`] = t[`${kind}Soft`]
    vars[`--cb-${kind}-dim`] = t[`${kind}Dim`]
    vars[`--cb-${kind}-ink`] = t[`${kind}Ink`]
    vars[`--cb-${kind}-on`] = t[`${kind}On`]
  })
  return vars
}

const CB_VAR_KEYS = Object.keys(colorToneCssVars(deriveColorTones(MONO_DEFAULT_ACCENT, MONO_DEFAULT_BG_DARK, true)))

/** Remove every inline --cb-* override (when leaving the color family). */
export function clearColorToneVars(el: HTMLElement) {
  for (const key of CB_VAR_KEYS) el.style.removeProperty(key)
}

/** Read a derived semantic tone for canvas/SVG (Recharts) consumers. */
export function readColorTone(kind: SemanticKind, variant: '' | 'Soft' | 'Dim' = ''): string {
  const suffix = variant === 'Soft' ? '-soft' : variant === 'Dim' ? '-dim' : ''
  return readMonoVar(`--cb-${kind}${suffix}`, CANONICAL[kind])
}

// ── presets ────────────────────────────────────────────────────────────────
export interface MonoPreset {
  id: string
  name: string
  bg: string
  accent: string
  mode: 'dark' | 'light'
  /** Optional background for the opposite mode, so a preset can theme both. */
  bgAlt?: string
}

export const MONO_PRESETS: MonoPreset[] = [
  { id: 'amber', name: 'Amber', bg: '#0a0a0b', accent: '#f5b82e', mode: 'dark' },
  { id: 'acid', name: 'Acid', bg: '#0b0d0a', accent: '#c6f24e', mode: 'dark' },
  // E Ink: e-paper grey + near-black "ink" in light; reversed (near-black paper +
  // light-grey ink) in dark — the ink reverses automatically via the accent
  // contrast guard, the page does so via bgAlt.
  { id: 'eink', name: 'E Ink', bg: '#ece9e1', accent: '#1c1b18', mode: 'light', bgAlt: '#16140f' },
  { id: 'ion', name: 'Ion', bg: '#07080c', accent: '#4ea8ff', mode: 'dark' },
  { id: 'bone', name: 'Bone', bg: '#f6f4ee', accent: '#a55f12', mode: 'light' },
  { id: 'mint', name: 'Mint', bg: '#f1f4f1', accent: '#1d7a51', mode: 'light' },
]

export type SankeyStyle = 'patterns' | 'colors'

/** True when a Caisibo mono theme is active (set by ThemeProvider on <html>). */
export function isMonoMode(): boolean {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute('data-caisibo-mono')
}

/** True when a Caisibo color theme is active (derived multi-hue palette). */
export function isColorMode(): boolean {
  return typeof document !== 'undefined' && document.documentElement.hasAttribute('data-caisibo-color')
}

/** Read a live `--mono-*` custom property off <html> (for canvas/SVG consumers). */
export function readMonoVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && HEX_RE.test(v.trim())
}

export function normalizeHex(v: string, fallback: string): string {
  return isHexColor(v) ? (v.trim().length === 4
    ? '#' + v.trim().slice(1).split('').map((c) => c + c).join('')
    : v.trim().toLowerCase()) : fallback
}
